import type { QuotaLimit } from "@/lib/billing/quota";
import type { AnalyticsContext } from "./envelope";
import { EVENTS } from "./registry";
import type { Track, TrackResult } from "./track";

/**
 * Stage E monetization event seam (M12 §6 "Monetization / paid-trigger
 * instrumentation"). The quota-warning + paywall UI (M12-B) fire the CANONICAL
 * §6 shapes through these helpers so the conversion funnel learns which gate
 * drives revenue:
 *
 *   quota_warning_shown { quota_type, usage, limit }
 *   paywall_hit         { trigger, quota_type, usage }   ← the §6 required shape
 *   upgrade_clicked      { trigger, plan_code, checkout_attempt_id }
 *   checkout_started     { plan_code, trigger, checkout_attempt_id,
 *                          checkout_session_id } (server-confirmed only)
 *
 * `trigger` is the ENUMERATED paid trigger the Conversion funnel segments by
 * (never a free-form string). This module is pure + client-safe (no Node/DB
 * imports) so both the "use client" paywall component and any server gate emit
 * through it; the caller injects the `track` seam + `AnalyticsContext`.
 */

/** The enumerated §6 paid triggers the conversion funnel segments by. */
export const PAYWALL_TRIGGERS = {
  SAVE_QUOTA_HIT: "save_quota_hit",
  REFRESH_QUOTA_HIT: "refresh_quota_hit",
  REMOVE_BRANDING: "remove_branding",
  LIVE_EMBED: "live_embed",
  NL_IN_EMBED: "nl_in_embed",
  PAID_GEOCODE_FALLBACK: "paid_geocode_fallback",
  /** FAIL-480: folders & tags gate. Its own trigger (not LIVE_EMBED) since it
   *  doesn't key on the live-map lever the way the other M10 capabilities do. */
  ORGANIZE: "organize",
  /** MAP-116: private/unlisted visibility gate (`quota.ts`'s `private_share`
   *  QuotaLimit). Its own trigger, fired by both the SharePanel dropdown and
   *  the editor ShareModal parity gate. */
  PRIVATE_SHARE: "private_share",
  /** MAP-116: the SharePanel password pro-row. Analytics-only trigger: there
   *  is no `QuotaLimit`/storage check backing it, just the generic Pro modal
   *  firing through `emitPaywallHit`. */
  SHARE_PASSWORD: "share_password",
  /** MAP-116: the ExportPanel CSV/KML/GeoJSON buttons. Analytics-only trigger,
   *  same reasoning as SHARE_PASSWORD above. */
  DATA_EXPORT: "data_export",
  /** API-key issuance and programmatic map access. Kept separate from live_embed
   *  so its paywall copy and conversion performance can be measured directly. */
  API_ACCESS: "api_access",
  /** 2026-07-15: the free-tier per-map location cap (`quota.ts`'s `map_points`
   *  QuotaLimit). Fired by the create/save gate when a free map exceeds the cap. */
  MAP_POINTS: "map_points",
  /** Satellite can be explored and saved in a draft, then gates at publish. */
  SATELLITE_BASEMAP: "satellite_basemap",
  /** Pro-line access to Ask your map. */
  ASK_MAP: "ask_map",
} as const;

export type PaywallTrigger = (typeof PAYWALL_TRIGGERS)[keyof typeof PAYWALL_TRIGGERS];

/**
 * The storage-keyed quota limit → the enumerated paid trigger. The M10 paid
 * capabilities (live/crm sync, api) resolve to `live_embed` (they gate on the
 * same live-map lever); `nl_in_embed` / `paid_geocode_fallback` are contextual
 * triggers with no quota-limit source, fired directly by their own gates.
 */
const LIMIT_TO_TRIGGER: Record<QuotaLimit, PaywallTrigger> = {
  saved_maps: PAYWALL_TRIGGERS.SAVE_QUOTA_HIT,
  live_maps: PAYWALL_TRIGGERS.LIVE_EMBED,
  refresh_cadence: PAYWALL_TRIGGERS.REFRESH_QUOTA_HIT,
  watermark: PAYWALL_TRIGGERS.REMOVE_BRANDING,
  live_sync: PAYWALL_TRIGGERS.LIVE_EMBED,
  crm_sync: PAYWALL_TRIGGERS.LIVE_EMBED,
  scheduled_refresh: PAYWALL_TRIGGERS.REFRESH_QUOTA_HIT,
  api_access: PAYWALL_TRIGGERS.API_ACCESS,
  organize: PAYWALL_TRIGGERS.ORGANIZE,
  private_share: PAYWALL_TRIGGERS.PRIVATE_SHARE,
  map_points: PAYWALL_TRIGGERS.MAP_POINTS,
  satellite_basemap: PAYWALL_TRIGGERS.SATELLITE_BASEMAP,
};

/** The `quota_type` prop for a limit — the storage lever the gate keyed on. */
const LIMIT_TO_QUOTA_TYPE: Record<QuotaLimit, string> = {
  saved_maps: "saved_maps",
  live_maps: "live_maps",
  refresh_cadence: "refresh_cadence",
  watermark: "branding",
  live_sync: "live_maps",
  crm_sync: "live_maps",
  scheduled_refresh: "refresh_cadence",
  api_access: "api_access",
  organize: "organize",
  private_share: "private_share",
  map_points: "map_points",
  satellite_basemap: "satellite_basemap",
};

/** Map a storage-keyed quota limit to its enumerated conversion trigger. */
export function quotaLimitToTrigger(limit: QuotaLimit): PaywallTrigger {
  return LIMIT_TO_TRIGGER[limit];
}

/** Map a storage-keyed quota limit to its `quota_type` funnel prop. */
export function quotaLimitToQuotaType(limit: QuotaLimit): string {
  return LIMIT_TO_QUOTA_TYPE[limit];
}

/**
 * Fire `quota_warning_shown { quota_type, usage, limit }` — the pre-gate nudge
 * (before a quota is actually hit). `limit` may be null (unlimited); the
 * registry treats a present-null as satisfied.
 */
export function emitQuotaWarningShown(
  track: Track,
  ctx: AnalyticsContext,
  props: { quotaType: string; usage: number; limit: number | null },
): TrackResult {
  return track(
    EVENTS.QUOTA_WARNING_SHOWN,
    { quota_type: props.quotaType, usage: props.usage, limit: props.limit },
    ctx,
  );
}

/**
 * Fire the CANONICAL `paywall_hit { trigger, quota_type, usage }` — the §6
 * required shape M12-A registered. `limit`/`plan_code` ride along as optional
 * enrichment when known.
 */
export function emitPaywallHit(
  track: Track,
  ctx: AnalyticsContext,
  props: { trigger: PaywallTrigger; quotaType: string; usage: number; limit?: number | null; planCode?: string },
): TrackResult {
  const payload: Record<string, unknown> = {
    trigger: props.trigger,
    quota_type: props.quotaType,
    usage: props.usage,
  };
  if (props.limit !== undefined && props.limit !== null) payload.limit = props.limit;
  if (props.planCode) payload.plan_code = props.planCode;
  return track(EVENTS.PAYWALL_HIT, payload, ctx);
}

/** Fire `upgrade_clicked { trigger, plan_code }` — the upgrade CTA was clicked. */
export function emitUpgradeClicked(
  track: Track,
  ctx: AnalyticsContext,
  props: { trigger: PaywallTrigger; planCode?: string; checkoutAttemptId?: string },
): TrackResult {
  const payload: Record<string, unknown> = { trigger: props.trigger };
  if (props.planCode) payload.plan_code = props.planCode;
  if (props.checkoutAttemptId) payload.checkout_attempt_id = props.checkoutAttemptId;
  return track(EVENTS.UPGRADE_CLICKED, payload, ctx);
}

/**
 * Fire the server-confirmed `checkout_started` event after Stripe returns a
 * usable Checkout Session. Callers must never invoke this on CTA click.
 */
export function emitCheckoutStarted(
  track: Track,
  ctx: AnalyticsContext,
  props: {
    planCode: string;
    checkoutAttemptId: string;
    checkoutSessionId: string;
    trigger?: PaywallTrigger | string;
    quotaType?: string;
  },
): TrackResult {
  const payload: Record<string, unknown> = {
    plan_code: props.planCode,
    checkout_attempt_id: props.checkoutAttemptId,
    checkout_session_id: props.checkoutSessionId,
  };
  if (props.trigger) payload.trigger = props.trigger;
  if (props.quotaType) payload.quota_type = props.quotaType;
  return track(EVENTS.CHECKOUT_STARTED, payload, ctx);
}

/** Fire only the CTA intent. Checkout start is emitted server-side after Stripe succeeds. */
export function emitPaywallUpgradeClicked(
  track: Track,
  ctx: AnalyticsContext,
  props: { trigger: PaywallTrigger; planCode: string; checkoutAttemptId: string },
): TrackResult {
  return emitUpgradeClicked(track, ctx, props);
}

/** Fire a safely classified failure without leaking provider error text. */
export function emitCheckoutFailed(
  track: Track,
  ctx: AnalyticsContext,
  props: {
    checkoutAttemptId: string;
    failureCode: string;
    planCode?: string;
    trigger?: PaywallTrigger | string;
    quotaType?: string;
    failurePhase?: "client_request" | "server_create";
    httpStatus?: number;
  },
): TrackResult {
  const payload: Record<string, unknown> = {
    checkout_attempt_id: props.checkoutAttemptId,
    failure_code: props.failureCode,
  };
  if (props.planCode) payload.plan_code = props.planCode;
  if (props.trigger) payload.trigger = props.trigger;
  if (props.quotaType) payload.quota_type = props.quotaType;
  if (props.failurePhase) payload.failure_phase = props.failurePhase;
  if (props.httpStatus) payload.http_status = props.httpStatus;
  return track(EVENTS.CHECKOUT_FAILED, payload, ctx);
}
